import React, {useState, useEffect, useCallback, useRef, useMemo} from "react";
import dayjs from "dayjs";
import axios from "../../../../axiosConfig";

// Import new components
import MobileWeekSelector from "./MobileWeekSelector";
import MobileDayTimeline from "./MobileDayTimeline";
import MobileTimePicker from "./MobileTimePicker";

// Import hooks
import { useMobileCalendarView } from "../../../../hooks/calendar/mobile/useMobileCalendarView";
import { useMobileTimeSelection } from "../../../../hooks/calendar/mobile/useMobileTimeSelection";

// Import utilities
import { mergeCalendarEvents } from "../../../../utils/calendar/calendarHelpers";
import { createTemporaryLockAndOpenModal } from "../../../../utils/calendar/timeCalculations";

// Import existing modals (that are already mobile-responsive)
import DeleteConfirmationModal from "../../DeleteConfirmationModal";
import BookTrainingModal from "../../BookTrainingModal";
import EditTrainingModal from "../../EditTrainingModal";
import TrainingDetailsModal from "../modals/TrainingDetailsModal";
import EditTemporaryLockModal from "../modals/EditTemporaryLockModal";
import EditTrainingLockModal from "../modals/EditTrainingLockModal";
import PendingRequestsModal from "../../PendingRequestsModal";

import "../../../../styles/MobileTrainerCalendar.css";

const MobileTrainerCalendar = ({
                                   currentWeekStart,
                                   selectedTrainer,
                                   trainers, // Add this prop
                                   workingHours,
                                   pendingBookings,
                                   confirmedTrainings,
                                   groupTrainings,
                                   lockedSlots,
                                   isLockMode,
                                   selectedLockSlots,
                                   setSelectedLockSlots,
                                   services,
                                   halls,
                                   schoolSettings,
                                   onPrevWeek,
                                   onNextWeek,
                                   refreshCalendar,
                                   onSaveLock,
                                   onCancelLock,
                                   onEditTemporaryLock,
                                   onDeleteTemporaryLock,
                                   onTrainerChange, // Add this prop
                                   onLockModeToggle // Add this prop
                               }) => {
    // Use mobile calendar view hook
    const {
        selectedDay,
        setSelectedDay,
        dayTimeRange,
        getMobileHourHeight,
        goToPreviousDay,
        goToNextDay,
        goToToday,
        getSelectedDayEvents
    } = useMobileCalendarView(workingHours, currentWeekStart);

    // Modal states
    const [pendingModalOpen, setPendingModalOpen] = useState(false);
    const [bookingModalOpen, setBookingModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [editLockModalOpen, setEditLockModalOpen] = useState(false);
    const [editTempLockModalOpen, setEditTempLockModalOpen] = useState(false);
    const [timePickerOpen, setTimePickerOpen] = useState(false);

    // Selected items for modals
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [selectedBookingSlot, setSelectedBookingSlot] = useState(null);
    const [trainingToEdit, setTrainingToEdit] = useState(null);
    const [trainingToDelete, setTrainingToDelete] = useState(null);
    const [selectedTrainingDetails, setSelectedTrainingDetails] = useState(null);
    const [lockToEdit, setLockToEdit] = useState(null);
    const [tempLockToEdit, setTempLockToEdit] = useState(null);

    // Swipe state for week selector
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [isSwipeAnimating, setIsSwipeAnimating] = useState(false);

    // Use time selection hook
    const {
        selectedStartTime,
        selectedEndTime,
        handleSlotSelect,
        clearSelection,
        openTimePicker: openTimePickerFromHook,
        closeTimePicker
    } = useMobileTimeSelection({
        workingHours,
        selectedDate: selectedDay,
        existingEvents: getSelectedDayEvents(mergeCalendarEvents({
            confirmedTrainings,
            groupTrainings,
            lockedSlots
        })),
        onTimeSelect: (time) => {
            if (isLockMode) {
                handleCreateTemporaryLock(time);
            } else {
                handleOpenBookingModal(time, time.add(60, 'minute'));
            }
        }
    });

    useEffect(() => {
        console.log('=== MOBILE CALENDAR DATA DEBUG ===');
        console.log('Selected Day:', selectedDay.format('YYYY-MM-DD dddd'));
        console.log('All Pending Bookings:', pendingBookings);
        console.log('Confirmed Trainings:', confirmedTrainings);
        console.log('=========================');
    }, [selectedDay, pendingBookings, confirmedTrainings]);

    // Merge all calendar events
    const dayPendingBookings = pendingBookings.filter(booking => {
        const bookingDate = dayjs(booking.slot);
        return bookingDate.isSame(selectedDay, 'day');
    });

    // Merge all calendar events
    const allEvents = useMemo(() => {
        // Get base events
        const baseEvents = mergeCalendarEvents({
            confirmedTrainings,
            groupTrainings,
            lockedSlots
        });

        // Filter pending bookings for selected day only
        const todaysPendingBookings = pendingBookings.filter(booking => {
            const bookingDate = dayjs(booking.slot);
            const isSameDay = bookingDate.isSame(selectedDay, 'day');

            // Debug log
            if (booking.bookingCount > 0) {
                console.log('Checking pending booking:', {
                    slot: booking.slot,
                    bookingDate: bookingDate.format('YYYY-MM-DD HH:mm'),
                    selectedDay: selectedDay.format('YYYY-MM-DD'),
                    isSameDay
                });
            }

            return isSameDay;
        });

        // Add only today's pending bookings
        const pendingEvents = pendingBookings
            .filter(booking => {
                const bookingDate = dayjs(booking.slot);
                return bookingDate.isSame(selectedDay, 'day');
            })
            .map((booking, index) => ({
                // Include end time in the ID to make it unique
                id: `pending-${booking.slot}-${booking.slotEndTime || index}`,
                startTime: booking.slot,
                endTime: booking.slotEndTime || dayjs(booking.slot).add(60, 'minute').format("YYYY-MM-DDTHH:mm:ss"),
                bookingCount: booking.bookingCount,
                type: 'pending'
            }));

        return [...baseEvents, ...pendingEvents];
    }, [confirmedTrainings, groupTrainings, lockedSlots, pendingBookings, selectedDay]);

    // Add temporary locks when in lock mode
    const eventsWithTempLocks = [
        ...allEvents,
        ...(isLockMode && selectedLockSlots ? selectedLockSlots.map((lock, index) => ({
            id: `temp-lock-${index}`,
            ...lock,
            isLocked: true,
            isTemporary: true
        })) : [])
    ];

    // Handle day navigation with animation
    const handlePrevDay = useCallback(() => {
        const prevDay = selectedDay.subtract(1, 'day');
        const weekStart = currentWeekStart;
        const weekEnd = currentWeekStart.add(6, 'day');

        if (prevDay.isBefore(weekStart)) {
            // Need to go to previous week
            setIsSwipeAnimating(true);
            setSwipeOffset(100);

            setTimeout(() => {
                onPrevWeek();
                setSelectedDay(weekEnd); // Select last day of previous week
                setSwipeOffset(0);
                setIsSwipeAnimating(false);
            }, 200);
        } else {
            goToPreviousDay();
        }
    }, [selectedDay, currentWeekStart, onPrevWeek, goToPreviousDay, setSelectedDay]);

    const handleNextDay = useCallback(() => {
        const nextDay = selectedDay.add(1, 'day');
        const weekStart = currentWeekStart;
        const weekEnd = currentWeekStart.add(6, 'day');

        if (nextDay.isAfter(weekEnd)) {
            // Need to go to next week
            setIsSwipeAnimating(true);
            setSwipeOffset(-100);

            setTimeout(() => {
                onNextWeek();
                setSelectedDay(weekStart.add(7, 'day')); // Select first day of next week
                setSwipeOffset(0);
                setIsSwipeAnimating(false);
            }, 200);
        } else {
            goToNextDay();
        }
    }, [selectedDay, currentWeekStart, onNextWeek, goToNextDay, setSelectedDay]);

    // Event handlers
    const handleEventClick = useCallback((event) => {
        if (event.bookingCount && event.bookingCount > 0) {
            setSelectedSlot({
                startTime: dayjs(event.startTime),
                endTime: dayjs(event.endTime)
            });
            setPendingModalOpen(true);
        } else if (event.confirmed && event.dogDTO) {
            setSelectedTrainingDetails(event);
            setDetailsModalOpen(true);
        } else if (event.isLocked && !event.isTemporary) {
            setLockToEdit(event);
            setEditLockModalOpen(true);
        } else if (event.isTemporary) {
            const lockIndex = selectedLockSlots.findIndex(lock =>
                lock.startTime === event.startTime && lock.endTime === event.endTime
            );
            if (lockIndex >= 0) {
                onEditTemporaryLock(lockIndex, event);
            }
        }
    }, [selectedLockSlots, onEditTemporaryLock]);

    const handleTimeSlotClick = useCallback((startTime, endTime) => {
        if (isLockMode) {
            handleCreateTemporaryLock(startTime);
        } else {
            handleOpenBookingModal(startTime, endTime);
        }
    }, [isLockMode]);

    const handleCreateTemporaryLock = useCallback((clickTime) => {
        if (!selectedTrainer?.id) return;

        // Clear any previous tempLockToEdit first
        setTempLockToEdit(null);

        const snappedStartTime = dayjs(clickTime);
        let endTime = snappedStartTime.add(1, 'hour');

        // Get day bounds for the clicked time's date, not the selected day
        const clickedDay = snappedStartTime.startOf('day');
        const dayOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][clickedDay.day()];
        const dayWorkingHours = workingHours.find(wh => wh.dayOfWeek === dayOfWeek);

        if (dayWorkingHours) {
            const [endHour, endMinute] = dayWorkingHours.closingTime.split(':').map(Number);
            const dayEndTime = clickedDay.hour(endHour).minute(endMinute).second(0);

            // Only limit if end time exceeds working hours
            if (endTime.isAfter(dayEndTime)) {
                endTime = dayEndTime;
            }
        }

        const newLock = {
            startTime: snappedStartTime.format("YYYY-MM-DDTHH:mm:ss"),
            endTime: endTime.format("YYYY-MM-DDTHH:mm:ss"),
            trainerId: selectedTrainer.id,
        };

        // Use functional update
        setSelectedLockSlots(prev => {
            const newIndex = prev.length;
            const updatedLocks = [...prev, newLock];

            // Set the tempLockToEdit with fresh data
            setTempLockToEdit({
                lockIndex: newIndex,
                lockData: {
                    startTime: newLock.startTime,
                    endTime: newLock.endTime,
                    trainerId: newLock.trainerId,
                    isTemporary: true
                }
            });

            // Open modal after state is set
            setEditTempLockModalOpen(true);

            return updatedLocks;
        });
    }, [selectedTrainer?.id, workingHours]);

    const handleOpenBookingModal = useCallback((startTime, endTime) => {
        setSelectedBookingSlot({
            startTime: dayjs(startTime),
            endTime: endTime || dayjs(startTime).add(60, 'minute')
        });
        setBookingModalOpen(true);
    }, []);

    const handleEditTraining = useCallback((training) => {
        setTrainingToEdit(training);
        setEditModalOpen(true);
    }, []);

    const handleDeleteTraining = useCallback((training) => {
        setTrainingToDelete(training);
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!trainingToDelete) return;

        const token = localStorage.getItem("jwt");

        try {
            if (trainingToDelete.isLocked) {
                await axios.delete(`/api/v1/training/lock/${trainingToDelete.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert("Zablokowany slot został usunięty.");
            } else {
                await axios.post(`/api/v1/training/${trainingToDelete.id}/decline`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert("Trening usunięty pomyślnie.");
            }

            refreshCalendar();
        } catch (err) {
            console.error("Error deleting:", err);
            alert("Błąd przy usuwaniu.");
        }

        setDeleteModalOpen(false);
        setTrainingToDelete(null);
    }, [trainingToDelete, refreshCalendar]);

    // Handle modals close
    const handleDetailsModalClose = () => {
        setDetailsModalOpen(false);
        setSelectedTrainingDetails(null);
    };

    const handleEditLockModalClose = () => {
        setEditLockModalOpen(false);
        setLockToEdit(null);
    };

    const handleEditTempLockModalClose = () => {
        setEditTempLockModalOpen(false);
        setTempLockToEdit(null);
    };

    const handleSaveTemporaryLock = (newTimes) => {
        if (tempLockToEdit && selectedLockSlots.length > tempLockToEdit.lockIndex) {
            const updatedLocks = [...selectedLockSlots];
            updatedLocks[tempLockToEdit.lockIndex] = {
                ...updatedLocks[tempLockToEdit.lockIndex],
                startTime: newTimes.startTime,
                endTime: newTimes.endTime,
            };
            setSelectedLockSlots(updatedLocks);
        }
    };

    // Ref for the week selector swipe handling
    const weekSelectorRef = useRef(null);

    // Swipe handling for week navigation
    useEffect(() => {
        const weekSelector = weekSelectorRef.current;
        if (!weekSelector) return;

        let startX = null;
        let startY = null;
        let isHorizontal = null;

        const handleStart = (e) => {
            if (isSwipeAnimating) return;

            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            isHorizontal = null;
        };

        const handleMove = (e) => {
            if (!startX || isSwipeAnimating) return;

            const touch = e.touches[0];
            const deltaX = touch.clientX - startX;
            const deltaY = touch.clientY - startY;

            // Only determine direction once
            if (isHorizontal === null && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
                isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
            }

            // Only handle if it's determined to be horizontal
            if (isHorizontal === true) {
                e.preventDefault(); // Prevent scrolling only for horizontal gestures
                const constrainedDelta = Math.max(-100, Math.min(100, deltaX));
                setSwipeOffset(constrainedDelta);
            }
        };

        const handleEnd = (e) => {
            if (!startX || isHorizontal !== true || isSwipeAnimating) {
                startX = null;
                startY = null;
                isHorizontal = null;
                setSwipeOffset(0);
                return;
            }

            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - startX;
            const threshold = 50;

            setIsSwipeAnimating(true);

            if (Math.abs(deltaX) > threshold) {
                // Trigger navigation
                if (deltaX > 0) {
                    onPrevWeek(); // Swipe right = previous week
                    // Set selected day to first day of previous week
                    const prevWeekStart = currentWeekStart.subtract(1, 'week');
                    setSelectedDay(prevWeekStart);
                } else {
                    onNextWeek(); // Swipe left = next week
                    // Set selected day to first day of next week
                    const nextWeekStart = currentWeekStart.add(1, 'week');
                    setSelectedDay(nextWeekStart);
                }
            }

            // Reset everything
            setTimeout(() => {
                setSwipeOffset(0);
                setIsSwipeAnimating(false);
                startX = null;
                startY = null;
                isHorizontal = null;
            }, 200);
        };

        weekSelector.addEventListener('touchstart', handleStart, { passive: true });
        weekSelector.addEventListener('touchmove', handleMove, { passive: false });
        weekSelector.addEventListener('touchend', handleEnd, { passive: true });
        weekSelector.addEventListener('touchcancel', handleEnd, { passive: true });

        return () => {
            weekSelector.removeEventListener('touchstart', handleStart);
            weekSelector.removeEventListener('touchmove', handleMove);
            weekSelector.removeEventListener('touchend', handleEnd);
            weekSelector.removeEventListener('touchcancel', handleEnd);
        };
    }, [isSwipeAnimating, onPrevWeek, onNextWeek]);

    return (
        <div className="mobile-calendar-view" style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'auto', // Changed from minHeight: '100vh' to height: 'auto'
        }}>

            {/* FIXED: Mobile Controls - positioned at the top */}
            <div className="mobile-calendar-controls">
                {/* Trainer Picker */}
                <div className="mobile-trainer-picker">
                    <label>Wybierz trenera:</label>
                    <select
                        value={selectedTrainer?.id || ""}
                        onChange={onTrainerChange}
                    >
                        <option value="">-- wybierz --</option>
                        {trainers?.map(t => (
                            <option key={t.id} value={t.id}>
                                {t.firstName} {t.lastName}
                            </option>
                        ))}
                    </select>
                </div>

                {/* FIXED: Lock Mode Controls - SINGLE ROW under trainer picker, NO hint text */}
                {isLockMode ? (
                    <div className="mobile-lock-controls">
                        <button
                            className="mobile-lock-save-btn"
                            onClick={onSaveLock}
                        >
                            Zapisz blokady ({selectedLockSlots.length})
                        </button>
                        <button
                            className="mobile-lock-cancel-btn"
                            onClick={onCancelLock}
                        >
                            Anuluj
                        </button>
                    </div>
                ) : (
                    <button
                        className="mobile-lock-mode-btn"
                        onClick={onLockModeToggle}
                    >
                        Zablokuj wybrane godziny
                    </button>
                )}

                {/* NOTE: NO hint text on mobile - completely removed */}
            </div>

            <div
                ref={weekSelectorRef}
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    touchAction: 'pan-y',
                }}
            >
                <MobileWeekSelector
                    ref={weekSelectorRef}
                    currentWeekStart={currentWeekStart}
                    selectedDay={selectedDay}
                    onDaySelect={setSelectedDay}
                    onPrevWeek={onPrevWeek}
                    onNextWeek={onNextWeek}
                    swipeOffset={swipeOffset}
                    isSwipeAnimating={isSwipeAnimating}
                    events={eventsWithTempLocks}
                />
            </div>

            {/* Day header */}
            <div className="mobile-day-header">
                <h2 className="mobile-day-title">
                    {selectedDay.format('dddd, DD MMMM YYYY')}
                </h2>
                {dayTimeRange.start && dayTimeRange.end && (
                    <div className="mobile-working-hours">
                        Godziny pracy: {dayTimeRange.start.format('HH:mm')} - {dayTimeRange.end.format('HH:mm')}
                    </div>
                )}
            </div>

            {/* Day timeline */}
            <MobileDayTimeline
                selectedDay={selectedDay}
                events={eventsWithTempLocks}
                workingHours={workingHours}
                isLockMode={isLockMode}
                selectedLockSlots={selectedLockSlots}
                onEventClick={handleEventClick}
                onEventEdit={handleEditTraining}
                onEventDelete={handleDeleteTraining}
                onSlotClick={handleTimeSlotClick}
                onEditLock={(lock) => {
                    setLockToEdit(lock);
                    setEditLockModalOpen(true);
                }}
                onEditTemporaryLock={onEditTemporaryLock}
                onDeleteTemporaryLock={onDeleteTemporaryLock}
                selectedTrainer={selectedTrainer}
            />

            {/* Modals */}
            {pendingModalOpen && (
                <PendingRequestsModal
                    trainerId={selectedTrainer?.id}
                    schoolId={selectedTrainer?.schoolId}
                    slotTime={selectedSlot?.startTime}
                    slotEndTime={selectedSlot?.endTime}
                    onClose={() => setPendingModalOpen(false)}
                    refreshCalendar={refreshCalendar}
                    allowReserveList={schoolSettings?.allowReserveList || false}
                />
            )}

            {bookingModalOpen && selectedTrainer && (
                <BookTrainingModal
                    isOpen={bookingModalOpen}
                    onClose={() => setBookingModalOpen(false)}
                    refreshCalendar={refreshCalendar}
                    trainerId={selectedTrainer.id}
                    schoolId={selectedTrainer.schoolId}
                    slotTime={selectedBookingSlot?.startTime}
                    endTime={selectedBookingSlot?.endTime}
                    services={services}
                    halls={halls}
                />
            )}

            {editModalOpen && trainingToEdit && selectedTrainer && (
                <EditTrainingModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    schoolId={selectedTrainer.schoolId}
                    trainingId={trainingToEdit.id}
                    refreshCalendar={refreshCalendar}
                />
            )}

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                itemName={
                    trainingToDelete?.isLocked
                        ? `zablokowany termin ${dayjs(trainingToDelete.startTime).format("HH:mm")} - ${dayjs(trainingToDelete.endTime).format("HH:mm")}`
                        : trainingToDelete
                            ? `trening ${dayjs(trainingToDelete.startTime).format("HH:mm")} - ${dayjs(trainingToDelete.endTime).format("HH:mm")} dla ${trainingToDelete.dogDTO?.name || 'nieznany'}`
                            : ""
                }
            />

            {detailsModalOpen && selectedTrainingDetails && (
                <TrainingDetailsModal
                    isOpen={detailsModalOpen}
                    onClose={handleDetailsModalClose}
                    training={selectedTrainingDetails}
                    onEdit={handleEditTraining}
                    onDelete={handleDeleteTraining}
                />
            )}

            {editLockModalOpen && lockToEdit && (
                <EditTrainingLockModal
                    isOpen={editLockModalOpen}
                    onClose={handleEditLockModalClose}
                    lock={lockToEdit}
                    refreshCalendar={refreshCalendar}
                    onDelete={handleDeleteTraining}
                />
            )}

            {editTempLockModalOpen && tempLockToEdit && (
                <EditTemporaryLockModal
                    isOpen={editTempLockModalOpen}
                    onClose={handleEditTempLockModalClose}
                    lock={tempLockToEdit.lockData}
                    onSave={handleSaveTemporaryLock}
                />
            )}

            {/* Mobile time picker for custom time selection */}
            <MobileTimePicker
                isOpen={timePickerOpen}
                onClose={() => setTimePickerOpen(false)}
                onConfirm={(startTime, endTime) => {
                    if (isLockMode) {
                        const newLock = {
                            startTime: startTime.format("YYYY-MM-DDTHH:mm:ss"),
                            endTime: endTime.format("YYYY-MM-DDTHH:mm:ss"),
                            trainerId: selectedTrainer.id,
                        };
                        setSelectedLockSlots(prev => [...prev, newLock]);
                    } else {
                        handleOpenBookingModal(startTime, endTime);
                    }
                }}
                initialStartTime={selectedStartTime}
                initialEndTime={selectedEndTime}
                minTime={dayTimeRange.start?.format('HH:mm')}
                maxTime={dayTimeRange.end?.format('HH:mm')}
                title={isLockMode ? "Wybierz czas blokady" : "Wybierz godziny treningu"}
            />

        </div>
    );
};

export default MobileTrainerCalendar;